import { Observable, switchMap } from 'rxjs';
import { tap, shareReplay, first } from 'rxjs/operators';
import { Injectable, Inject } from '@angular/core';
import { NgRedux } from './ng-redux.service';
import { WINDOW } from '../app/injection-tokens';
import { REDUX_LOCAL_STORAGE_KEY, ReduxMiddleWares } from './middleware';
import { appReducer } from './reducers';
import { reduxBatch } from '@manaflair/redux-batch';
import { AuthenticationService } from '../services/authentication.service';
import {
    IStateFiltersSlice,
    IStateSortsSlice,
    IStatePaginationSlice,
    IStateTimeSlice,
    IStateMetricSeriesSlice,
    IStateAggregationMethodSlice,
    IStateCapacityFullMethodSlice,
    IStateClusterSlice,
    IStateCollectorSlice,
    IStateMetricTypeSlice,
    IStateSelectionSlice,
    IStateGroupSelectionSlice,
    IStateResizerSlice,
    IStateFleetType,
    IStateItemType,
    IStateTabSlice,
    IStateRegionSlice,
    IStateDisasterRecoveryToggledItemsSlice,
    IStateDisasterRecoverySelectedPolicySlice,
    IStateDisasterRecoverySelectedPlanSlice,
    IStateDisasterRecoverySelectedReportSlice,
    IStateDisasterRecoveryFiltersSlice,
    IStateDisasterRecoveryClusterSlice,
    IStateEdgeServiceAgentCardsCollapseState,
    IStatePureProtectFileId,
    IStatePureProtectSiteId,
    IStatePureProtectOpportunityId,
    IStatePureProtectAdminView,
    IStatePureProtectVmPercentage,
    IStatePureProtectTargetSite,
    IStatePanelTabSlice,
    IStateStepUpTokenSlice,
    IStateConfigurationAdvisorGroupBySlice,
    IStateConfigurationAdvisorViewSlice,
    IStateConfigurationAdvisorLensSlice,
    IStateConfigurationAdvisorWindowDaysSlice,
    IStateConfigurationAdvisorPercentileSlice,
    IStateConfigurationAdvisorPerformanceGroupingSlice,
} from './types';

export interface IState {
    filters?: IStateFiltersSlice;
    sorts?: IStateSortsSlice;
    paginations?: IStatePaginationSlice;
    times?: IStateTimeSlice;
    metricSeries?: IStateMetricSeriesSlice;
    aggregationMethod?: IStateAggregationMethodSlice;
    capacityFullMethod?: IStateCapacityFullMethodSlice;
    selection?: IStateSelectionSlice;
    groupSelection?: IStateGroupSelectionSlice;
    cluster?: IStateClusterSlice;
    collector?: IStateCollectorSlice;
    metricType?: IStateMetricTypeSlice;
    resizerSize?: IStateResizerSlice;
    fleetType?: IStateFleetType;
    itemType?: IStateItemType;
    tab?: IStateTabSlice;
    configurationAdvisorGroupBy?: IStateConfigurationAdvisorGroupBySlice;
    configurationAdvisorView?: IStateConfigurationAdvisorViewSlice;
    configurationAdvisorLens?: IStateConfigurationAdvisorLensSlice;
    configurationAdvisorWindowDays?: IStateConfigurationAdvisorWindowDaysSlice;
    configurationAdvisorPercentile?: IStateConfigurationAdvisorPercentileSlice;
    configurationAdvisorPerformanceGrouping?: IStateConfigurationAdvisorPerformanceGroupingSlice;
    edgeServiceAgentCardsCollapseState?: IStateEdgeServiceAgentCardsCollapseState;
    panel?: IStatePanelTabSlice;
    region?: IStateRegionSlice;
    stepUpTokens?: IStateStepUpTokenSlice;
    disasterRecoveryToggledItems?: IStateDisasterRecoveryToggledItemsSlice;
    disasterRecoverySelectedPolicy?: IStateDisasterRecoverySelectedPolicySlice;
    disasterRecoverySelectedPlan?: IStateDisasterRecoverySelectedPlanSlice;
    disasterRecoverySelectedReport?: IStateDisasterRecoverySelectedReportSlice;
    disasterRecoveryCluster?: IStateDisasterRecoveryClusterSlice;
    disasterRecoveryFilters?: IStateDisasterRecoveryFiltersSlice;
    pureProtectFileId?: IStatePureProtectFileId;
    pureProtectSiteId?: IStatePureProtectSiteId;
    pureProtectOpportunityId?: IStatePureProtectOpportunityId;
    pureProtectAdminView?: IStatePureProtectAdminView;
    pureProtectVmPercentage?: IStatePureProtectVmPercentage;
    pureProtectTargetSite?: IStatePureProtectTargetSite;
}

@Injectable({ providedIn: 'root' })
export class PureRedux {
    private init$: Observable<void>;

    constructor(
        private readonly ngRedux: NgRedux<IState>,
        private readonly middlewares: ReduxMiddleWares,
        @Inject(WINDOW) private readonly window: Window,
        private readonly authenticationService: AuthenticationService,
    ) {}

    /**
     * Returns an observable that completes when redux has been configured.
     * Redux gets configured only once, and so this will normally return an observable that will emit immediately.
     */
    init(): Observable<void> {
        if (!this.init$) {
            this.init$ = this.authenticationService.baseKeyGenerated$.pipe(
                first(),
                switchMap(_ => this.authenticationService.isLoggedIn()),
                tap(isLoggedIn => {
                    // Only configure redux if logged in
                    if (isLoggedIn) {
                        this.configureRedux();
                    }
                }),
                shareReplay<void>(1),
            );
        }
        return this.init$;
    }

    private configureRedux(): void {
        let initialState: IState = {};
        if (this.window.localStorage) {
            const value = this.window.localStorage[`${this.window.pure1.basekey}:${REDUX_LOCAL_STORAGE_KEY}`];
            if (value) {
                try {
                    initialState = JSON.parse(value);
                } catch (e) {
                    console.error('Error occurred parsing redux state from local storage', e);
                }
            }
        }
        this.ngRedux.configureStore(
            appReducer,
            initialState,
            [this.middlewares.consoleLogger, this.middlewares.localStorage],
            [<any>reduxBatch],
        );
    }
}
